import axios from 'axios'
import { defineStore } from 'pinia'
import { BASE_URL } from '@/environment/environment'
import { toApiDate } from '@/utils/formatters'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { useFilesStore } from '@/modules/files/stores/filesStore'

import type { IAuditRegisterStoreState } from '@/modules/audits/stores/useAuditRegisterStore/types/IAuditRegisterStoreState'
import type { IAuditRegisterFilters } from '@/modules/audits/stores/useAuditRegisterStore/types/IAuditRegisterFilters'
import type { IAuditRegisterItem } from '@/modules/audits/stores/useAuditRegisterStore/types/IAuditRegisterItem'
import type { IFilterChoiceItem } from '@/modules/universalStats/types/IFilterChoiceItem'
import type { IUniversalTreeSelectModule } from '@/types/IUniversalTreeSelectModule'
import type { IAudit } from '@/modules/audits/stores/useAuditRegisterStore/types/IAudit'
import type { IAuditProcessPayload } from '@/modules/audits/stores/useAuditRegisterStore/types/IAuditProcessPayload'
import type { IQualityControlStandardTreeItem } from '@/modules/audits/types/IQualityControlStandardTreeItem'
import { mapQualityControlTreeToFilterChoices } from '@/modules/audits/types/IQualityControlStandardTreeItem'
import type { IAuditCardAssignmentFilters } from '@/modules/audits/stores/useAuditRegisterStore/types/IAuditCardAssignmentFilters'
import type { IAuditAssignment } from '@/modules/audits/stores/useAuditRegisterStore/types/IAuditAssignment'
import type { IRealtimePayload } from '@/modules/centrifuge/types/IRealtimePayload'
import { getBlobErrorMessage } from '@/modules/documents-new/utils/fileHelpers'
import { onboardingEntityApiUrl } from '@/modules/onboarding/utils/demoApi'

export const useAuditRegisterStore = defineStore('auditRegister', {
  state: (): IAuditRegisterStoreState => ({
    audits: [],
    nextCursor: null,
    totalAuditsCount: 0,
    itemsPerPage: 25,
    page: 1,
    currentSortString: null,
    currentAudit: {
      audit_type: 'planned',
      name: '',
      departments: [],
      planned_start: '',
      planned_end: '',
      manager: null,
      audit_assignments: [],
    } as IAudit,
    currentAuditedDepartment: null,
    editableAudit: null,
    processingAuditItems: [],

    filters: {
      numbers: [],
      standards: [],
      parents: [],
      departments: [],
      auditors: [],
      managers: [],
      planned_start: null,
      planned_end: null,
      audit_type: [],
      status: [],
      is_overdue: null,
      requires_action: null,
      search: '',
    } as IAuditRegisterFilters,

    filterChoices: {},
    searchChoices: {},
    filterCursors: {},
    filterLoading: {},
    filterStrategies: {},

    treeChoices: {},
    treeSearchChoices: {},
    treeCursors: {},
    treeLoading: {},
    currentStandard: null,

    auditCardAssignmentContext: null,
    auditCardAssignments: [],
    auditCardAssignmentsLoading: false,
    auditCardAssignmentsSort: null,
    auditCardNonCompliancesCount: 0,
    auditCardCompliancesCount: 0,
    auditCardNotApplicableCount: 0,
    auditCardCompliancePercent: null,
    auditCardAssignmentFilters: {
      parent_numbers: [],
      parent_names: '',
      auditors: [],
      status: [],
    },
    auditCardAssignmentFilterChoices: {},
    auditCardAssignmentSearchChoices: {},
    auditCardAssignmentFilterCursors: {},
    auditCardAssignmentFilterLoading: {},
    auditCardAssignmentFilterStrategies: {},
  }),

  getters: {
    hasActiveFilters(state): boolean {
      const f = state.filters
      if (!f) return false
      if (f.search && f.search.trim() !== '') return true
      if (f.planned_start !== null || f.planned_end !== null) return true
      if (f.is_overdue !== null && f.is_overdue !== undefined) return true
      if (f.requires_action !== null && f.requires_action !== undefined) return true
      if (Array.isArray(f.numbers) && f.numbers.length > 0) return true
      if (Array.isArray(f.standards) && f.standards.length > 0) return true
      if (Array.isArray(f.parents) && f.parents.length > 0) return true
      if (Array.isArray(f.departments) && f.departments.length > 0) return true
      if (Array.isArray(f.auditors) && f.auditors.length > 0) return true
      if (Array.isArray(f.managers) && f.managers.length > 0) return true
      if (Array.isArray(f.audit_type) && f.audit_type.length > 0) return true
      if (Array.isArray(f.status) && f.status.length > 0) return true
      return false
    },
  },

  actions: {
    async fetchFilterChoices(fieldKey: string, search = '', cursor?: string) {
      if (!fieldKey) return
      this.filterLoading[fieldKey] = true

      try {
        const params: any = { field: fieldKey }

        if (search) params.search_field = search
        if (cursor) params.cursor = cursor

        const filters = this.filters as IAuditRegisterFilters

        if (fieldKey !== 'numbers' && filters.numbers.length) {
          params.numbers = filters.numbers.join(',')
        }

        if (fieldKey !== 'parents' && filters.parents?.length) {
          params.parents = filters.parents.join(',')
        }

        if (fieldKey !== 'standards' && filters.standards?.length) {
          params.standards = filters.standards.join(',')
        }

        if (fieldKey !== 'departments' && filters.departments.length) {
          params.departments = filters.departments.join(',')
        }

        if (fieldKey !== 'auditors' && filters.auditors?.length) {
          params.auditors = filters.auditors.join(',')
        }

        if (fieldKey !== 'managers' && filters.managers?.length) {
          params.managers = filters.managers.join(',')
        }

        if (fieldKey !== 'audit_type' && filters.audit_type?.length) {
          params.audit_type = filters.audit_type.join(',')
        }

        if (fieldKey !== 'status' && filters.status.length) {
          params.status = filters.status.join(',')
        }

        if (filters.is_overdue !== null) {
          params.is_overdue = filters.is_overdue
        }

        if (fieldKey !== 'planned_start' && filters.planned_start) {
          params.planned_start = formatDateOnly(filters.planned_start) // YYYY-MM-DD
        }

        if (fieldKey !== 'planned_end' && filters.planned_end) {
          params.planned_end = formatDateOnly(filters.planned_end)
        }

        const response = await axios.get(`${BASE_URL}/api/audit/audit/filter-choices/`, { params })
        const { results, next, strategy } = response.data

        this.filterStrategies[fieldKey] = strategy || 'independent'

        if (search) {
          this.searchChoices[fieldKey] = cursor
            ? [...(this.searchChoices[fieldKey] || []), ...results]
            : results
        } else {
          this.filterChoices[fieldKey] = cursor
            ? [...(this.filterChoices[fieldKey] || []), ...results]
            : results
        }

        this.filterCursors[fieldKey] = next
      } catch (e) {
        console.error(`Ошибка загрузки фильтров реестра [${fieldKey}]:`, e)
      } finally {
        this.filterLoading[fieldKey] = false
      }
    },

    async fetchAudits(cursor?: string, itemsPerPage?: number, throwOnError = false): Promise<void> {
      const feedbackStore = useFeedbackStore()
      if (this.audits.length === 0) {
        feedbackStore.isGlobalLoading = true
      }

      try {
        const params: Record<string, any> = {}

        if (this.currentSortString) {
          params.sort = this.currentSortString
        }

        if (itemsPerPage) {
          this.itemsPerPage = itemsPerPage
        }

        if (this.itemsPerPage) {
          params.page_size = this.itemsPerPage
        }

        if (cursor) params.cursor = cursor

        const filters = this.filters as IAuditRegisterFilters

        if (filters.search) {
          params.search = filters.search
        }

        if (filters.numbers.length) {
          params.numbers = filters.numbers.join(',')
        }

        if (filters.standards.length) {
          params.standards = filters.standards.join(',')
        }

        if (filters.parents.length) {
          params.parents = filters.parents.join(',')
        }

        if (filters.departments.length) {
          params.departments = filters.departments.join(',')
        }

        if (filters.auditors.length) {
          params.auditors = filters.auditors.join(',')
        }

        if (filters.audit_type.length) {
          params.audit_type = filters.audit_type.join(',')
        }

        if (filters.managers.length) {
          params.managers = filters.managers.join(',')
        }

        if (filters.status.length) {
          params.status = filters.status.join(',')
        }

        if (filters.is_overdue !== null) {
          params.is_overdue = filters.is_overdue
        }

        if (filters.requires_action !== null && filters.requires_action !== undefined) {
          params.requires_action = filters.requires_action
        }

        if (filters.planned_start) {
          params.planned_start = filters.planned_start
        }

        if (filters.planned_end) {
          params.planned_end = filters.planned_end
        }

        const res = await axios.get(`${BASE_URL}/api/audit/audit/`, { params })
        const results: IAuditRegisterItem[] = res.data.results ?? res.data

        this.nextCursor = res.data.next

        if (cursor) {
          this.audits = [...this.audits, ...results]
        } else {
          this.audits = results
        }

        this.totalAuditsCount = res.data.count
      } catch (error) {
        if (throwOnError) throw error
        console.error(`❌ Ошибка загрузки реестра аудитов: ${String(error)}`)
        feedbackStore.showToast({ type: 'error', message: getApiError(error, 'Не удалось загрузить реестр аудитов') })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async submitAuditProcess(auditId: number, payload: IAuditProcessPayload): Promise<void> {
      const feedbackStore = useFeedbackStore()
      const filesStore = useFilesStore()

      feedbackStore.isGlobalLoading = true

      try {
        const url = `${BASE_URL}/api/audit/audit/${auditId}/process/`
        const form = new FormData()

        form.append('assignment_id', String(payload.assignment_id))
        form.append('point_id', String(payload.point_id))
        form.append('department_id', String(payload.department_id))

        if (payload.status) {
          form.append('status', payload.status)
        }

        form.append('comment', payload.comment || '')

        if (payload.not_comply_category) {
          form.append('not_comply_category', payload.not_comply_category)
        }

        if (payload.not_comply_level !== null && payload.not_comply_level !== undefined) {
          form.append('not_comply_level', String(payload.not_comply_level))
        }

        if (payload.remediation_recommendations) {
          form.append('remediation_recommendations', payload.remediation_recommendations)
        }

        if (payload.remediation_deadline) {
          form.append('remediation_deadline', payload.remediation_deadline)
        }

        if (payload.delete_files && payload.delete_files.length) {
          payload.delete_files.forEach(id => {
            form.append('delete_files', String(id))
          })
        }

        const validFiles = filesStore.normalizeToFileArray(payload.add_files)
        if (validFiles.length) {
          validFiles.forEach(file => {
            form.append('add_files', file)
          })
        }

        const { data } = await axios.post(url, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })

        if (data.results) {
          this.processingAuditItems = data.results
        }

        feedbackStore.showToast({
          type: 'success',
          message: 'Результат сохранен',
          time: 3000,
        })
      } catch (error) {
        const message = getApiError(error, 'Не удалось выполнить действие')
        logger.error('submitAuditProcess_error', {
          store: 'auditRegister',
          function: 'submitAuditProcess',
          error: `❌ Ошибка: ${error}`,
          payload,
        })
        feedbackStore.showToast({ type: 'error', message })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /*
    * Завершение отчётов аудитора: делает их неизменяемыми, а по отчётам
    * со статусом «Не соответствует» бэкенд создаёт записи документооборота.
    */
    async completeAuditReports(ids: number[]): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        await axios.post(`${BASE_URL}/api/audit/reports/complete/`, { ids })

        feedbackStore.showToast({ type: 'success', message: 'Проверка завершена' })
      } catch (error) {
        const message = getApiError(error, 'Не удалось завершить проверку')
        logger.error('completeAuditReports_error', {
          store: 'auditRegister',
          function: 'completeAuditReports',
          error: `❌ Ошибка: ${error}`,
          ids,
        })
        feedbackStore.showToast({ type: 'error', message })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async fetchAuditById(id: number): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const { data } = await axios.get<IAudit>(`${BASE_URL}/api/audit/audit/${id}/`)

        this.currentAudit = {
          ...data,
          departments: data.departments ?? [],
          audit_assignments: data.audit_assignments ?? [],
        }
        this.editableAudit = JSON.parse(JSON.stringify(this.currentAudit))
      } catch (error) {
        console.error('❌ Ошибка загрузки аудита:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить аудит'),
        })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /* Сырой список назначений для формы редактирования (без статусов и department) */
    async fetchAuditAssignmentsRaw(auditId: number): Promise<void> {
      const feedbackStore = useFeedbackStore()

      try {
        const { data } = await axios.get(
          onboardingEntityApiUrl('audit', auditId, `/audit/audit/${auditId}/assignments/raw/`),
        )

        this.currentAudit.audit_assignments = data
        this.editableAudit = JSON.parse(JSON.stringify(this.currentAudit))
      } catch (error) {
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить назначения аудита'),
        })
      }
    },

    async createAudit(): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      const plannedStart = toApiDate(this.currentAudit.planned_start)
      const plannedEnd = toApiDate(this.currentAudit.planned_end)

      try {
        const payload = {
          name: this.currentAudit.name,
          audit_type: this.currentAudit.audit_type,
          planned_start: plannedStart,
          planned_end: plannedEnd,
          departments: this.currentAudit.departments.map(d => ({ id: d.id })),
          manager: this.currentAudit.manager ? { id: this.currentAudit.manager.id } : undefined,
          audit_assignments: this.currentAudit.audit_assignments.map(a => ({
            employee: { id: a.employee.id },
            points: a.points.map(c => ({ id: c.id })),
          })),
        }

        await axios.post(`${BASE_URL}/api/audit/audit/`, payload)

        feedbackStore.showToast({ type: 'success', message: 'Аудит успешно создан' })
        this.resetAudit()
        await feedbackStore.goToParentRoute()
      } catch (error) {
        const message = getApiError(error, 'Не удалось создать аудит')
        logger.error('createAudit_error', {
          store: 'auditRegister',
          function: 'createAudit',
          error: `❌ Ошибка: ${error}`,
          payload: { audit_type: this.currentAudit.audit_type },
        })
        feedbackStore.showToast({ type: 'error', message })
      } finally {
        await this.fetchAudits()
        feedbackStore.isGlobalLoading = false
      }
    },

    async processAudit(
      id: number,
      departmentId: string,
      assignmentId: number,
      parentId?: string,
    ): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const params = new URLSearchParams()

        params.append('departments', departmentId)

        if (assignmentId && !Number.isNaN(assignmentId)) {
          params.append('assignment', String(assignmentId))
        }

        if (parentId) {
          params.append('parent', parentId)
        }

        const url = onboardingEntityApiUrl('audit', id, `/audit/audit/${id}/process/?${params.toString()}`)
        const { data } = await axios.get(url)

        this.processingAuditItems = data.results
      } catch (error) {
        console.error('❌ Ошибка при выполнении /process/:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось выполнить действие'),
        })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    async updateAudit(id: number): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      const plannedStart = toApiDate(this.currentAudit.planned_start)
      const plannedEnd = toApiDate(this.currentAudit.planned_end)

      try {
        const payload = {
          name: this.currentAudit.name,
          audit_type: this.currentAudit.audit_type,
          planned_start: plannedStart,
          planned_end: plannedEnd,
          departments: this.currentAudit.departments.map(d => ({ id: d.id })),
          manager: this.currentAudit.manager ? { id: this.currentAudit.manager.id } : undefined,
          audit_assignments: this.currentAudit.audit_assignments.map(a => ({
            ...(a.id && { id: a.id }),
            employee: { id: a.employee.id },
            points: a.points.map(c => ({ id: c.id })),
          })),
        }

        await axios.put(`${BASE_URL}/api/audit/audit/${id}/`, payload)

        feedbackStore.showToast({ type: 'success', message: 'Аудит успешно обновлён' })
        this.resetAudit()
        await feedbackStore.goToParentRoute()
      } catch (error) {
        console.error('❌ Ошибка при обновлении аудита:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось обновить аудит'),
        })
      } finally {
        await this.fetchAudits()
        feedbackStore.isGlobalLoading = false
      }
    },

    // Сброс фильтров
    resetFilters() {
      this.filters = {
        numbers: [],
        standards: [],
        parents: [],
        departments: [],
        auditors: [],
        managers: [],
        planned_start: null,
        planned_end: null,
        audit_type: [],
        status: [],
        is_overdue: null,
        requires_action: null,
        search: '',
      } as IAuditRegisterFilters
      this.currentSortString = null
      this.page = 1
    },

    async fetchTreeChoices(
      fieldKey: string,
      search?: string,
      cursor?: string,
      module?: IUniversalTreeSelectModule,
    ): Promise<void> {
      this.treeLoading[fieldKey] = true

      const urlMap: Record<string, string> = {
        'audit-standard': '/api/qualitycontrol/standard/',
        'audit-criterion': '/api/qualitycontrol/standard/',
      }

      const path = urlMap[module as string] || '/api/qualitycontrol/standard/'
      const url = `${BASE_URL}${path}`

      const params: any = {
        search: search || undefined,
        cursor: cursor || undefined,
      }

      if (module === 'audit-criterion') {
        params.depth = 'point'
      }

      try {
        const { data } = await axios.get(url, { params })

        let results = data.results as IQualityControlStandardTreeItem[]

        if (module === 'audit-criterion' && data.count === 1 && results.length === 1) {
          results = results[0].children || []
        }

        const formattedData = mapQualityControlTreeToFilterChoices(results)

        if (cursor) {
          this.treeChoices[fieldKey] = [
            ...(this.treeChoices[fieldKey] || []),
            ...formattedData,
          ]
        } else if (search) {
          this.treeSearchChoices[fieldKey] = formattedData
        } else {
          this.treeChoices[fieldKey] = formattedData
        }

        this.treeCursors[fieldKey] = data.next
      } catch (error) {
        console.error(`[TreeSelect Error] fieldKey: ${fieldKey}`, error)
      } finally {
        this.treeLoading[fieldKey] = false
      }
    },

    clearTreeChoices(fieldKey: string) {
      if (this.treeChoices[fieldKey]) delete this.treeChoices[fieldKey]
      if (this.treeSearchChoices[fieldKey]) delete this.treeSearchChoices[fieldKey]
      if (this.treeCursors[fieldKey]) delete this.treeCursors[fieldKey]
    },

    setAuditCardAssignmentContext(auditId: number, departmentId: string) {
      this.auditCardAssignmentContext = { auditId, departmentId }
    },

    resetAuditCardAssignmentFilters() {
      this.auditCardAssignmentFilters = {
        parent_numbers: [],
        parent_names: '',
        auditors: [],
        status: [],
      }
      this.auditCardAssignmentsSort = null
      this.auditCardNonCompliancesCount = 0
      this.auditCardCompliancesCount = 0
      this.auditCardNotApplicableCount = 0
      this.auditCardCompliancePercent = null
      this.auditCardAssignmentFilterChoices = {}
      this.auditCardAssignmentSearchChoices = {}
      this.auditCardAssignmentFilterCursors = {}
      this.auditCardAssignmentFilterLoading = {}
      this.auditCardAssignmentFilterStrategies = {}
    },

    async fetchAuditCardAssignmentFilterChoices(fieldKey: string, search = '', cursor?: string) {
      const ctx = this.auditCardAssignmentContext
      if (!fieldKey || !ctx) return

      this.auditCardAssignmentFilterLoading[fieldKey] = true

      try {
        const params: Record<string, string> = {
          field: fieldKey,
          department_id: ctx.departmentId,
        }

        if (search) params.search_field = search
        if (cursor) params.cursor = cursor

        const filters = this.auditCardAssignmentFilters as IAuditCardAssignmentFilters

        if (fieldKey !== 'parent_numbers' && filters.parent_numbers.length) {
          params.parent_numbers = filters.parent_numbers.join(',')
        }
        if (fieldKey !== 'auditors' && filters.auditors.length) {
          params.auditors = filters.auditors.join(',')
        }
        if (fieldKey !== 'status' && filters.status.length) {
          params.status = filters.status.join(',')
        }
        if (fieldKey !== 'parent_names' && filters.parent_names) {
          params.parent_names = filters.parent_names
        }

        const url = `${BASE_URL}/api/audit/audit/${ctx.auditId}/assignments/filter-choices/`
        const { data } = await axios.get(url, { params })
        const { results, next, strategy } = data

        this.auditCardAssignmentFilterStrategies[fieldKey] = strategy || 'independent'

        if (search) {
          this.auditCardAssignmentSearchChoices[fieldKey] = cursor
            ? [...(this.auditCardAssignmentSearchChoices[fieldKey] || []), ...results]
            : results
        } else {
          this.auditCardAssignmentFilterChoices[fieldKey] = cursor
            ? [...(this.auditCardAssignmentFilterChoices[fieldKey] || []), ...results]
            : results
        }

        this.auditCardAssignmentFilterCursors[fieldKey] = next
      } catch (e) {
        console.error(`Ошибка filter-choices назначений карточки [${fieldKey}]:`, e)
      } finally {
        this.auditCardAssignmentFilterLoading[fieldKey] = false
      }
    },

    async fetchAuditCardAssignments(): Promise<void> {
      const ctx = this.auditCardAssignmentContext
      if (!ctx?.departmentId) return

      this.auditCardAssignmentsLoading = true

      try {
        const params: Record<string, string> = {
          department_id: ctx.departmentId,
          page_size: '1000',
        }

        if (this.auditCardAssignmentsSort) {
          params.sort = this.auditCardAssignmentsSort
        }

        const filters = this.auditCardAssignmentFilters as IAuditCardAssignmentFilters

        if (filters.parent_numbers.length) {
          params.parent_numbers = filters.parent_numbers.join(',')
        }
        if (filters.auditors.length) {
          params.auditors = filters.auditors.join(',')
        }
        if (filters.status.length) {
          params.status = filters.status.join(',')
        }
        if (filters.parent_names) {
          params.parent_names = filters.parent_names
        }

        const url = onboardingEntityApiUrl('audit', ctx.auditId, `/audit/audit/${ctx.auditId}/assignments/`)
        const { data } = await axios.get(url, { params })

        this.auditCardAssignments = data.results ?? []
        this.auditCardNonCompliancesCount = data.non_compliances_count ?? 0
        this.auditCardCompliancesCount = data.compliances_count ?? 0
        this.auditCardNotApplicableCount = data.not_applicable_count ?? 0
        this.auditCardCompliancePercent = data.compliance_percent ?? null
      } catch (error) {
        const feedbackStore = useFeedbackStore()
        const message = getApiError(error, 'Не удалось загрузить назначения')
        feedbackStore.showToast({ type: 'error', message })
        this.auditCardAssignments = []
        this.auditCardNonCompliancesCount = 0
        this.auditCardCompliancesCount = 0
        this.auditCardNotApplicableCount = 0
        this.auditCardCompliancePercent = null
      } finally {
        this.auditCardAssignmentsLoading = false
      }
    },

    async exportChecklist(
      auditId: number,
      format: 'docx' | 'xlsx',
      employeeId: string,
    ): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const response = await axios.get(
          `${BASE_URL}/api/audit/audit/${auditId}/export-checklist/`,
          {
            params: { format, employee_id: employeeId },
            responseType: 'blob',
          },
        )

        const contentType = (response.headers['content-type'] as string) || 'application/octet-stream'
        const blob = new Blob([response.data], { type: contentType })
        const blobUrl = window.URL.createObjectURL(blob)

        const contentDisposition = (response.headers['content-disposition'] as string) || ''
        let fileName = `checklist_${auditId}.${format}`

        if (contentDisposition) {
          const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^; "']+)["']?/)
          if (match?.[1]) {
            fileName = decodeURIComponent(match[1])
          }
          else {
            const fallbackMatch = contentDisposition.match(/filename=["']?([^; "']+)["']?/)
            if (fallbackMatch?.[1]) {
              fileName = fallbackMatch[1]
            }
          }
        }

        const link = document.createElement('a')
        link.href = blobUrl
        link.setAttribute('download', fileName)
        link.click()
        window.URL.revokeObjectURL(blobUrl)

        feedbackStore.showToast({
          type: 'success',
          message: 'Чек-лист скачан',
        })
      } catch (error) {
        let message = getApiError(error, 'Не удалось скачать чек-лист')
        if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
          message = (await getBlobErrorMessage(error.response.data)) || message
        }
        feedbackStore.showToast({ type: 'error', message })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /* Список назначений для выбора аудитора в экспорте чек-листа (без мутации формы) */
    async fetchChecklistAssignments(auditId: number): Promise<IAuditAssignment[]> {
      try {
        const { data } = await axios.get(
          onboardingEntityApiUrl('audit', auditId, `/audit/audit/${auditId}/assignments/raw/`),
        )

        return data ?? []
      } catch (error) {
        const feedbackStore = useFeedbackStore()
        const message = getApiError(error, 'Не удалось загрузить список аудиторов')
        feedbackStore.showToast({ type: 'error', message })

        return []
      }
    },

    async exportAuditReport(
      auditId: number,
      format: 'docx' | 'xlsx',
      departmentId?: string,
    ): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const params: Record<string, string> = { format }
        if (departmentId) params.department_id = departmentId

        const response = await axios.get(
          `${BASE_URL}/api/audit/audit/${auditId}/export/`,
          {
            params,
            responseType: 'blob',
          },
        )

        const contentType = (response.headers['content-type'] as string) || 'application/octet-stream'
        const blob = new Blob([response.data], { type: contentType })
        const blobUrl = window.URL.createObjectURL(blob)

        const contentDisposition = (response.headers['content-disposition'] as string) || ''
        let fileName = `audit_report_${auditId}.${format}`

        if (contentDisposition) {
          const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^; "']+)["']?/)
          if (match?.[1]) {
            fileName = decodeURIComponent(match[1])
          }
          else {
            const fallbackMatch = contentDisposition.match(/filename=["']?([^; "']+)["']?/)
            if (fallbackMatch?.[1]) {
              fileName = fallbackMatch[1]
            }
          }
        }

        const link = document.createElement('a')
        link.href = blobUrl
        link.setAttribute('download', fileName)
        link.click()
        window.URL.revokeObjectURL(blobUrl)

        feedbackStore.showToast({
          type: 'success',
          message: 'Отчёт скачан',
        })
      } catch (error) {
        let message = getApiError(error, 'Не удалось скачать отчёт')
        if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
          message = (await getBlobErrorMessage(error.response.data)) || message
        }
        feedbackStore.showToast({ type: 'error', message })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * audit_created / updated / deleted.
     * Строка таблицы берётся из сообщения: это AuditListSerializer, те же поля, что у таблицы.
     * Открытая форма — GET по id: в сообщении нет назначений и editable_fields.
     */
    async applyAuditRealtime(payload: IRealtimePayload): Promise<void> {
      const feedbackStore = useFeedbackStore()
      if (!payload?.event_type || !payload?.data) return

      const data = payload.data as IAuditRegisterItem | { id: number }
      const auditId = Number(data.id)
      if (!auditId) return

      if (this.hasActiveFilters) {
        await this.fetchAudits(undefined, undefined, false)
        if (this.currentAudit && Number(this.currentAudit.id) === auditId) {
          await this.fetchAuditById(auditId)
          await this.fetchAuditCardAssignments()
        }
        return
      }

      const idx = this.audits.findIndex((a: IAuditRegisterItem) => a.id === auditId)

      switch (payload.event_type) {
        case 'audit_created': {
          const audit = data as IAuditRegisterItem
          if (idx >= 0) this.audits.splice(idx, 1)
          this.audits = [audit, ...this.audits]
          this.totalAuditsCount++
          feedbackStore.showToast({
            type: 'info',
            message: `Создана проверка № ${audit.id}`,
            time: 5000,
          })
          break
        }
        case 'audit_updated': {
          const audit = data as IAuditRegisterItem
          if (idx >= 0)
            this.audits.splice(idx, 1, audit)
          else
            this.audits = [audit, ...this.audits]

          if (this.currentAudit && Number(this.currentAudit.id) === auditId) {
            await this.fetchAuditById(auditId)
            await this.fetchAuditCardAssignments()
          }
          break
        }
        case 'audit_deleted': {
          if (idx >= 0) {
            this.audits.splice(idx, 1)
            this.totalAuditsCount = Math.max(0, this.totalAuditsCount - 1)
            feedbackStore.showToast({
              type: 'warning',
              message: `Проверка № ${auditId} удалена`,
              time: 5000,
            })
          }
          break
        }
      }
    },

    resetAudit() {
      this.currentAudit = {
        audit_type: 'planned',
        name: '',
        departments: [],
        planned_start: '',
        planned_end: '',
        manager: null,
        audit_assignments: [],
      } as IAudit
      this.editableAudit = null
      this.currentStandard = null
    },

    updateRequiresAction(id: number, requiresAction: boolean) {
      const audit = this.audits.find(a => Number(a.id) === id)
      if (audit) {
        audit.requires_action = requiresAction
      }
    },
  },
})
